import { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import "dayjs/locale/ru";

import ProfileNavigation from "../../../components/common/ProfileNavigation";
import SEO from "../../../components/SEO";
import ErrorHandler from "../../../components/common/ErrorHandler";
import {
    ErrorHandlerContext,
    ErrorHandlerContextProvider,
} from "../../../contexts/ErrorHandlerContext";
import NotificationCard from "../../../components/doctor/NotificationCard";
import useDoctorNotifications from "../../../hooks/useDoctorNotifications";
import { NOTIFICATIONS_POLL_MS } from "../../../contexts/DoctorNotificationsContext";
import useUser, { getAuthorized, isUserDoctor } from "../../../hooks/User";
import { ROUTES } from "../../../routes";
import { NOTIFICATIONS_DISCLAIMER } from "../../../constants/notificationsDisclaimer";

import "./notifications-page.css";

dayjs.extend(relativeTime);
dayjs.locale("ru");

const TABS = [
    { key: "new", label: "Новые", params: { is_read: false } },
    { key: "all", label: "Все", params: {} },
    { key: "archive", label: "Архив", params: { is_read: true } },
];

const seoProps = {
    title: "Уведомления — Spiria",
    description: "Оповещения о состоянии пациентов после опросов",
};

function NotificationsPageContent() {
    const { loading: userLoading, error: userError, data } = useUser();
    const navigate = useNavigate();
    const { addNotification } = useContext(ErrorHandlerContext);
    const { refresh: refreshGlobal, markRead, markAllRead } = useDoctorNotifications();

    const [activeTab, setActiveTab] = useState("new");
    const [alerts, setAlerts] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [markingId, setMarkingId] = useState(null);

    const [filterClass, setFilterClass] = useState("");
    const [filterPatientId, setFilterPatientId] = useState("");

    useEffect(() => {
        if (userLoading) return;
        if (userError) navigate(ROUTES.AUTH, { viewTransition: true });
        if (!isUserDoctor(data)) navigate(ROUTES.PROFILE, { viewTransition: true });
    }, [userLoading, userError, data, navigate]);

    const fetchList = useCallback(async () => {
        const tab = TABS.find((t) => t.key === activeTab);
        if (!tab) return;
        setIsLoading(true);

        const params = { ...tab.params, limit: 50, st: 0 };
        if (filterClass) {
            params.situation_class = filterClass;
        }
        if (filterPatientId && !isNaN(parseInt(filterPatientId, 10))) {
            params.patient_id = parseInt(filterPatientId, 10);
        }

        try {
            const { data: response } = await getAuthorized("/doctor/risk_alerts", { params });
            setAlerts(response?.alerts ?? []);
            setTotalCount(response?.total_count ?? 0);
        } catch (err) {
            const msg =
                err?.response?.data?.error_description ||
                "Не удалось загрузить список";
            addNotification(msg);
        } finally {
            setIsLoading(false);
        }
    }, [activeTab, filterClass, filterPatientId, addNotification]);

    useEffect(() => {
        if (!userLoading && isUserDoctor(data)) {
            fetchList();
        }
    }, [userLoading, data, fetchList]);

    const handleMarkRead = useCallback(
        async (alert) => {
            setMarkingId(alert.id);
            try {
                await markRead(alert.id);
                await fetchList();
                await refreshGlobal();
                addNotification({ msg: "Отмечено как прочитанное", type: "success" });
            } catch (err) {
                const msg =
                    err?.response?.data?.error_description ||
                    "Не удалось обновить статус";
                addNotification(msg);
            } finally {
                setMarkingId(null);
            }
        },
        [markRead, fetchList, refreshGlobal, addNotification]
    );

    const handleMarkAllRead = useCallback(async () => {
        setMarkingId("all");
        try {
            await markAllRead();
            await fetchList();
            await refreshGlobal();
            addNotification({ msg: "Все уведомления отмечены как прочитанные", type: "success" });
        } catch (err) {
            const msg =
                err?.response?.data?.error_description ||
                "Не удалось обновить статус";
            addNotification(msg);
        } finally {
            setMarkingId(null);
        }
    }, [markAllRead, fetchList, refreshGlobal, addNotification]);

    const handleOpenPatient = useCallback(
        (alert) => {
            navigate(ROUTES.PATIENTS, {
                state: { highlightPatientId: alert.patient_id },
                viewTransition: true,
            });
        },
        [navigate]
    );

    if (userLoading) {
        return (
            <>
                <SEO {...seoProps} />
                <ProfileNavigation isDoctor={isUserDoctor(data)} />
                <div className="doctor-notifications-page">
                    <div className="doctor-notifications-loading">Загрузка…</div>
                </div>
            </>
        );
    }

    return (
        <>
            <SEO {...seoProps} />
            <ProfileNavigation isDoctor={isUserDoctor(data)} />
            <ErrorHandler />
            <div className="doctor-notifications-page">
                <div className="doctor-notifications-container">
                    <header className="doctor-notifications-page-header">
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "16px", marginBottom: "12px" }}>
                            <h1 style={{ margin: 0 }}>Уведомления</h1>
                            {activeTab === "new" && alerts.length > 0 && (
                                <button
                                    type="button"
                                    className="doctor-notification-btn doctor-notification-btn--ghost"
                                    disabled={markingId === "all"}
                                    onClick={handleMarkAllRead}
                                >
                                    {markingId === "all" ? "..." : "Отметить все как прочитанные"}
                                </button>
                            )}
                        </div>
                        <p>
                            Уведомления появляются после оценки ответов пациента на опрос
                            (обычно в течение нескольких минут).
                        </p>
                        <p className="doctor-notifications-disclaimer" role="note">
                            {NOTIFICATIONS_DISCLAIMER}
                        </p>
                    </header>

                    <div className="doctor-notifications-tabs" role="tablist">
                        {TABS.map((tab) => (
                            <button
                                key={tab.key}
                                type="button"
                                role="tab"
                                aria-selected={activeTab === tab.key}
                                className={`doctor-notifications-tab ${activeTab === tab.key ? "active" : ""}`}
                                onClick={() => setActiveTab(tab.key)}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>

                    <div className="doctor-notifications-filters" style={{ display: "flex", gap: "16px", marginBottom: "20px", flexWrap: "wrap", alignItems: "center" }}>
                        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                            <label htmlFor="filter-class-select" style={{ fontSize: "12px", color: "var(--text-secondary)", fontWeight: 500 }}>Класс важности</label>
                            <select
                                id="filter-class-select"
                                value={filterClass}
                                onChange={(e) => setFilterClass(e.target.value)}
                                style={{ padding: "8px 12px", borderRadius: "6px", border: "1px solid var(--border-color)", background: "var(--bg-card)", color: "var(--text-primary)", fontSize: "14px", outline: "none" }}
                            >
                                <option value="">Все классы</option>
                                <option value="S3">S3 — Высокий риск</option>
                                <option value="S4">S4 — Нужно внимание</option>
                            </select>
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                            <label htmlFor="filter-patient-input" style={{ fontSize: "12px", color: "var(--text-secondary)", fontWeight: 500 }}>ID пациента</label>
                            <input
                                id="filter-patient-input"
                                type="text"
                                placeholder="Например: 42"
                                value={filterPatientId}
                                onChange={(e) => setFilterPatientId(e.target.value.replace(/\D/g, ""))}
                                style={{ padding: "8px 12px", borderRadius: "6px", border: "1px solid var(--border-color)", background: "var(--bg-card)", color: "var(--text-primary)", fontSize: "14px", outline: "none", width: "150px" }}
                            />
                        </div>
                        {(filterClass || filterPatientId) && (
                            <button
                                type="button"
                                onClick={() => {
                                    setFilterClass("");
                                    setFilterPatientId("");
                                }}
                                style={{ marginTop: "18px", padding: "8px 12px", background: "none", border: "none", color: "var(--text-secondary)", cursor: "pointer", fontSize: "14px" }}
                            >
                                Сбросить
                            </button>
                        )}
                    </div>

                    {isLoading ? (
                        <div className="doctor-notifications-loading">Загрузка списка…</div>
                    ) : alerts.length === 0 ? (
                        <div className="doctor-notifications-empty">
                            {activeTab === "new" ? (
                                <>
                                    <p className="doctor-notifications-empty__title">Нет новых уведомлений</p>
                                    <p className="doctor-notifications-empty__hint">
                                        После прохождения опроса пациентом оценка обычно появляется
                                        в течение <strong>1–3 минут</strong>. Список обновляется
                                        автоматически — можно оставить вкладку открытой.
                                    </p>
                                </>
                            ) : activeTab === "archive" ? (
                                <p className="doctor-notifications-empty__title">Архив пуст</p>
                            ) : (
                                <p className="doctor-notifications-empty__title">Уведомлений пока нет</p>
                            )}
                        </div>
                    ) : (
                        <>
                            <p className="doctor-notifications-page-header" style={{ marginBottom: 12 }}>
                                <small>Всего: {totalCount}</small>
                            </p>
                            <ul className="doctor-notifications-list">
                                {alerts.map((alert) => (
                                    <li key={alert.id} className="doctor-notifications-list-item">
                                        <NotificationCard
                                            alert={alert}
                                            markingRead={markingId === alert.id}
                                            onMarkRead={
                                                !alert.is_read ? handleMarkRead : undefined
                                            }
                                            onOpenPatient={handleOpenPatient}
                                        />
                                    </li>
                                ))}
                            </ul>
                        </>
                    )}

                    <p className="doctor-notifications-poll-hint">
                        Список обновляется автоматически примерно каждые{" "}
                        {Math.round(NOTIFICATIONS_POLL_MS / 1000)} с, пока вы в кабинете.
                    </p>
                </div>
            </div>
        </>
    );
}

export default function NotificationsPage() {
    return (
        <ErrorHandlerContextProvider>
            <NotificationsPageContent />
        </ErrorHandlerContextProvider>
    );
}
